import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import ColorField from 'app/frontend/base/components/form-fields/ColorField'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import { useTranslation } from 'react-i18next'
import { Globe, Info, Trash2, Undo2 } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import { useTeam } from 'app/frontend/base/config/context/team'
import TeamMemberLink, {
  RankColorPresentation,
} from 'app/frontend/base/components/TeamMemberLink'

export type EditableTeamRank = Partial<
  wowaudit.TeamRank & {
    _destroy?: boolean
  }
>

const name = 'team_ranks_attributes'

const TeamRankFormContent = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { watch, setValue } = useFormContext()
  const { append, remove } = useFieldArray({ name })

  const teamRanks: EditableTeamRank[] = watch(name)
  const previewMember = useMemo(() => {
    const member = team.members?.[0]
    if (!member) return undefined

    const fallbackColor = member.teamRank?.color ?? 'rgb(11, 74, 222)'

    return {
      ...member,
      teamRank: {
        ...member.teamRank,
        color: member.teamRank?.color ?? fallbackColor,
      },
    }
  }, [team.members, teamRanks])

  const columns: ColumnDef<EditableTeamRank>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          return (
            <TextField
              layout='stacked'
              name={`team_ranks_attributes.${info.row.index}.name`}
              className={`min-w-32 ${
                info.row.original._destroy ? 'opacity-30' : ''
              }`}
              style={{ color: info.row.original.color ?? undefined }}
              customLabel={''}
              customPlaceholder='Name'
            />
          )
        },
      },
      {
        id: 'for_alts',
        header: () => (
          <span className='flex items-center gap-2 whitespace-nowrap'>
            Type
            <Tooltip>
              <TooltipTrigger>
                <Info size={14} className='opacity-70' />
              </TooltipTrigger>
              <TooltipContent>
                Alts are grouped together with the main they belong to (if the
                owner has logged in to wowaudit)
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: (info) => {
          return (
            <SelectField
              layout='stacked'
              name={`team_ranks_attributes.${info.row.index}.for_alts`}
              transformOptions={false}
              options={[
                { label: 'Main', value: false },
                { label: 'Alt', value: true },
              ]}
              className={`min-w-40 ${
                info.row.original._destroy ? 'opacity-30' : ''
              }`}
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'roster_visibility',
        header: () => (
          <span className='flex items-center gap-2 whitespace-nowrap'>
            Show in roster setup
            <Tooltip>
              <TooltipTrigger>
                <Info size={14} className='opacity-70' />
              </TooltipTrigger>
              <TooltipContent>
                {t('settings.teamRankVisibility.roster_description')}
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: (info) => {
          return (
            <SelectField
              layout='stacked'
              name={`${name}.${info.row.index}.roster_visibility`}
              transformOptions={false}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
              className={`min-w-24 ${
                info.row.original._destroy ? 'opacity-30' : ''
              }`}
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'color',
        header: 'Colour',
        cell: (info) => {
          return (
            <div className={info.row.original._destroy ? 'opacity-30' : ''}>
              <ColorField
                layout='stacked'
                name={`${name}.${info.row.index}.color`}
                className='h-10 w-24'
                customLabel={''}
                customDescription=''
                format='rgb'
              />
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: (info) => {
          return (
            <div className='flex justify-end mt-0.5'>
              <Button
                type='button'
                size='sm'
                variant={
                  info.row.original._destroy ? 'secondary' : 'destructive'
                }
                onClick={() =>
                  info.row.original.id
                    ? setValue(
                        `${name}.${info.row.index}._destroy`,
                        !info.row.original._destroy,
                      )
                    : remove(info.row.index)
                }
              >
                {info.row.original._destroy ? (
                  <Undo2 size={14} />
                ) : (
                  <Trash2 size={14} />
                )}
              </Button>
            </div>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className='mb-8'>
      <h5>Team ranks</h5>
      <div className='flex flex-col gap-2'>
        <DataTable
          cellClassName='align-top px-2'
          includeHeader={teamRanks && teamRanks.length > 0}
          columns={columns}
          data={teamRanks ?? []}
        />

        <div className='flex justify-end mb-4'>
          <Button
            type='button'
            size='sm'
            className='mt-2'
            variant='outline'
            onClick={() =>
              append({
                name: 'New rank',
                for_alts: false,
                default: false,
                roster_visibility: true,
                raid_visibility: true,
                wishlist_visibility: true,
                spreadsheet_summary_visibility: true,
                spreadsheet_overview_visibility: true,
                spreadsheet_vault_visibility: true,
                spreadsheet_roster_visibility: true,
                spreadsheet_raids_visibility: true,
                spreadsheet_profession_visibility: true,
              })
            }
          >
            Add rank
          </Button>
        </div>

        <SettingField
          name='rankColorPresentation'
          layout='box'
          renderOptionPreview={({ option }) =>
            previewMember ? (
              <TeamMemberLink
                member={previewMember}
                iconType='class'
                disableNavigation
                rankColorPresentationOverride={option as RankColorPresentation}
              />
            ) : null
          }
        />

        <SelectField<EditableTeamRank>
          name='default_team_rank_id'
          className='min-w-[20rem]'
          options={teamRanks.filter((rank) => rank.id && !rank._destroy)}
          layout='box'
          optionLabelFn={(rank) => (
            <span style={{ color: rank.color ?? undefined }}>{rank.name}</span>
          )}
          optionValueKey='id'
          disableSorting
        />

        <h5>Member visibility</h5>

        <Card className='pt-3 pb-8 px-4'>
          <div className='flex gap-3 items-end mb-2'>
            <div className='w-40'>Visible in</div>
            {teamRanks.map((rank, index) => (
              <div
                key={index}
                style={{
                  height: `${Math.max(
                    Math.min(5.5, (rank.name ?? '').length / 2),
                    2.5,
                  )}rem`,
                }}
                className='w-8 flex items-end justify-center'
              >
                <div className='relative'>
                  <span
                    className='absolute bottom-0 left-1/2 rotate-[-35deg] whitespace-nowrap max-w-32 text-ellipsis overflow-hidden'
                    style={{
                      transformOrigin: 'center left',
                      color: rank.color ?? undefined,
                    }}
                  >
                    {rank.name}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {[
            'raid',
            'wishlist',
            'spreadsheet_summary',
            'spreadsheet_overview',
            'spreadsheet_vault',
            'spreadsheet_roster',
            'spreadsheet_raids',
            'spreadsheet_profession',
          ].map((section) => (
            <div
              key={section}
              className='flex gap-3 items-center border-t py-1'
            >
              <div className='w-40 flex items-center gap-2'>
                <Tooltip>
                  <TooltipTrigger>
                    {section.includes('spreadsheet') ? (
                      <img
                        src='https://data.wowaudit.com/img/sheets-logo.png'
                        className='w-5'
                      />
                    ) : (
                      <div className='w-5 flex items-center justify-center'>
                        <Globe size={16} />
                      </div>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {section.includes('spreadsheet')
                      ? 'Spreadsheet'
                      : 'Website'}
                  </TooltipContent>
                </Tooltip>

                {t(`settings.teamRankVisibility.${section}`)}
              </div>
              {teamRanks.map((rank, index) => (
                <div key={index} className='w-8 flex justify-center mt-1'>
                  {rank.for_alts && section === 'raid' ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <Info size={14} className='opacity-70 mb-1.5 ml-0.5' />
                      </TooltipTrigger>
                      <TooltipContent>
                        Alts are grouped together with the main they belong to
                        (if the owner has logged in to wowaudit)
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <CheckboxField
                      layout='stacked'
                      name={`team_ranks_attributes.${index}.${section}_visibility`}
                      customLabel={''}
                      customDescription={''}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </Card>
      </div>
    </div>
  )
}

export default TeamRankFormContent
